package ai.jessie.web.settings

import ai.jessie.shared.settings.OrganisationSettingsResponse
import ai.jessie.shared.settings.SaveOrganisationSettingsRequest
import ai.jessie.shared.settings.TaxonomyProposal
import ai.jessie.shared.settings.VatRateProposal

// Empty strings represent absent configuration, including the required extraction choice.
data class SettingsDraft(
    val fields: Map<String, String>,
    val vatRates: List<VatRateProposal>,
    val categories: List<TaxonomyProposal>,
    val folders: List<TaxonomyProposal>,
) {
    fun request(revision: Long) =
        SaveOrganisationSettingsRequest(
            expectedRevision = revision,
            name = fields.getValue("name"),
            timeZone = fields.getValue("timeZone"),
            address = fields.getValue("address"),
            countryCode = fields.getValue("countryCode"),
            iban = fields["iban"],
            vatNumber = fields["vatNumber"],
            invoicePrefix = fields.getValue("invoicePrefix"),
            nextInvoiceNumber = fields.getValue("nextInvoiceNumber").toInt(),
            defaultCurrency = fields.getValue("defaultCurrency"),
            invoiceLanguage = fields.getValue("invoiceLanguage"),
            paymentTermsDays = fields.getValue("paymentTermsDays").toInt(),
            automaticDocumentExtraction =
                fields.getValue("automaticDocumentExtraction").toBooleanStrict(),
            vatRates = vatRates,
            documentCategories = categories,
            documentFolders = folders,
        )

    fun values(): Map<String, String> =
        fields +
            buildMap {
                vatRates.forEachIndexed { index, row ->
                    val key = "vatRate[${row.vatRateId ?: "neu $index"}]"
                    put("$key.name", row.name)
                    put("$key.rate", row.rate)
                    put("$key.effectiveFrom", row.effectiveFrom)
                    put("$key.remove", row.remove.toString())
                }
                listOf("documentCategory" to categories, "documentFolder" to folders).forEach {
                    (kind, rows) ->
                    rows.forEachIndexed { index, row ->
                        val key = "$kind[${row.id ?: "neu $index"}]"
                        put("$key.name", row.name)
                        put("$key.active", row.active.toString())
                    }
                }
            }
}

internal fun OrganisationSettingsResponse.draft() =
    SettingsDraft(
        fields =
            linkedMapOf(
                "name" to name,
                "timeZone" to timeZone,
                "address" to address.orEmpty(),
                "countryCode" to countryCode.orEmpty(),
                "iban" to iban.orEmpty(),
                "vatNumber" to vatNumber.orEmpty(),
                "invoicePrefix" to invoicePrefix.orEmpty(),
                "nextInvoiceNumber" to nextInvoiceNumber?.toString().orEmpty(),
                "defaultCurrency" to defaultCurrency.orEmpty(),
                "invoiceLanguage" to invoiceLanguage.orEmpty(),
                "paymentTermsDays" to paymentTermsDays?.toString().orEmpty(),
                "automaticDocumentExtraction" to automaticDocumentExtraction?.toString().orEmpty(),
            ),
        vatRates =
            vatRates.map { VatRateProposal(it.name, it.rate, it.effectiveFrom, it.vatRateId) },
        categories = documentCategories.map { TaxonomyProposal(it.name, it.active, it.id) },
        folders = documentFolders.map { TaxonomyProposal(it.name, it.active, it.id) },
    )

// The full baseline/current/intended comparison remains visible during manual re-entry (A6).
data class SettingsConflict(
    val field: String,
    val previous: String?,
    val current: String?,
    val intended: String?,
    val conflict: Boolean,
)

internal fun settingsConflicts(
    baseline: OrganisationSettingsResponse,
    current: OrganisationSettingsResponse,
    intended: SettingsDraft,
): List<SettingsConflict> {
    val before = baseline.draft().values()
    val now = current.draft().values()
    val proposal = intended.values()
    return (before.keys + now.keys + proposal.keys).mapNotNull { field ->
        if (before[field] == now[field] && before[field] == proposal[field]) null
        else
            SettingsConflict(
                field,
                before[field],
                now[field],
                proposal[field],
                before[field] != now[field] &&
                    before[field] != proposal[field] &&
                    now[field] != proposal[field],
            )
    }
}
